import * as React from 'react';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { ModalView, ModalViewProps } from './Modal.view';
import { useKeypress } from '../../../hooks/useKeypress';
import { idGenerator } from '../../../helpers/idGenerator';
import { useDidMount } from '../../../hooks/useDidMount';
import { BreakpointsValuesEnum } from '../../../constants/breakpoints-values';
import { ModalsContext } from '../../../providers/ModalsProvider';
import { useCallbackPrompt } from '../../../hooks/useCallbackPrompt';
import { useBreakpoints } from '../../../hooks/useBreakpoints';

export type ModalControllerProps = ModalViewProps & {
  closeOnEsc?: boolean;
  closeOnClickOutside?: boolean;
  clickOnOutsideHandler?: VoidFunction;
};

export const ModalController = (props: ModalControllerProps): JSX.Element => {
  const modalId = useMemo(() => idGenerator('modal'), []);
  const { onClose, size } = props;
  const [modalsRef, modalsUpdater] = useContext(ModalsContext);
  const breakpoint = useBreakpoints();

  const onCloseAttempt = () => {
    const lastModalRef = modalsRef.current[modalsRef.current.length - 1];
    if (lastModalRef.id === modalId) {
      onClose();
    }
  };

  const breakpoints = useMemo<Array<keyof typeof BreakpointsValuesEnum>>(() => {
    switch (size) {
      case 'small':
      case 'hugging':
      case 'fluid':
      case undefined:
      default:
        return ['small'];
      case 'large':
        return ['small', 'medium'];
    }
  }, [size]);

  const { showPrompt, confirmNavigation, cancelNavigation } = useCallbackPrompt(modalId, onCloseAttempt);
  const navigationRef = useRef({ confirmNavigation, cancelNavigation });

  useDidMount(() => {
    if (modalId && !modalsRef.current.filter(item => item.id === modalId).length) {
      modalsUpdater({ type: 'add', value: modalId })(modalsRef);
    }
    return () => {
      modalsUpdater({ type: 'remove', value: modalId })(modalsRef);
    };
  });

  useEffect(() => {
    const lastModalRef = modalsRef.current[modalsRef.current.length - 1];
    if (showPrompt && lastModalRef.id === modalId) {
      if (breakpoints.includes(breakpoint)) {
        navigationRef.current.cancelNavigation();
      } else {
        navigationRef.current.confirmNavigation();
      }
    }
  }, [showPrompt, breakpoint, breakpoints, modalsRef, modalId]);

  const clickOnOutsideHandler = () => {
    // If True or not set, we close
    const lastModalRef = modalsRef.current[modalsRef.current.length - 1];
    if (
      props.closeOnClickOutside !== false &&
      !props.primaryButtonLoading &&
      !props.secondaryButtonLoading &&
      lastModalRef.id === modalId
    ) {
      onClose();
    }
  };

  const onEscapeHandler = (e?: KeyboardEvent) => {
    // If True or not set, we close
    const lastModalRef = modalsRef.current[modalsRef.current.length - 1];
    if (
      props.closeOnEsc !== false &&
      !props.primaryButtonLoading &&
      !props.secondaryButtonLoading &&
      lastModalRef.id === modalId
    ) {
      e && e.stopImmediatePropagation();
      onClose();
    }
  };

  useKeypress('Escape', onEscapeHandler);

  return <ModalView clickOnOutsideHandler={props.clickOnOutsideHandler || clickOnOutsideHandler} {...props} />;
};
